/**
 * Chart color palette — reads live CSS custom properties set by applyTheme().
 *
 * Use useChartColors() inside React components.
 * Use getChartColors() in plain utility functions (reportExport, etc.).
 */
import { useMemo } from "react";
import useAuthStore from "./store/authStore";

function _cssRgb(shade) {
  if (typeof document === "undefined") return "59 130 246";
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue(`--bh-${shade}`)
      .trim() || "59 130 246"
  );
}

function _hex(shade) {
  const rgb = _cssRgb(shade);
  return "#" + rgb.split(" ").map(v => parseInt(v).toString(16).padStart(2, "0")).join("");
}

export function getChartColors() {
  const LINE_COLORS = [_hex(800), _hex(700), _hex(600), _hex(500), _hex(300)];
  const COLOR_DARK    = _hex(800);
  const COLOR_PRIMARY = _hex(500);
  const COLOR_LIGHT   = _hex(300);

  return {
    LINE_COLORS,
    COLOR_DARK,
    COLOR_PRIMARY,
    COLOR_LIGHT,
    ROW_COLORS: LINE_COLORS,

    BLADE_PALETTE: [
      _hex(800), _hex(700),
      "#92400e", "#4c1d95", "#065f46",
    ],

    CASING_PALETTE: [
      _hex(300), _hex(500),
      "#f59e0b", "#8b5cf6", "#34d399",
    ],

    WRAP_PALETTE: [
      _hex(800), _hex(700),
      "#92400e", "#4c1d95", "#065f46",
    ],

    COMBINED_MECO_PALETTE: [
      { hub: _hex(800), shroud: _hex(600) },
      { hub: _hex(700), shroud: _hex(500) },
      { hub: "#92400e", shroud: "#f59e0b" },
      { hub: "#4c1d95", shroud: "#8b5cf6" },
      { hub: "#065f46", shroud: "#34d399" },
    ],

    DESIGN_COLORS: [
      COLOR_PRIMARY,
      "#f59e0b", "#8b5cf6", "#ef4444",
      "#3b82f6", "#10b981", "#f97316",
    ],

    BAR_PALETTE: [
      _hex(800), _hex(600), _hex(300),
      "#3b82f6", "#8b5cf6", "#f59e0b",
      "#ef4444", "#10b981", "#f97316", "#6366f1",
    ],
  };
}

export function useChartColors() {
  const entity = useAuthStore(s => s.user?.entity);
  // Re-computes when entity changes; by then applyTheme() has already
  // updated the CSS vars (called in authStore before set()).
  return useMemo(() => getChartColors(), [entity]); // eslint-disable-line react-hooks/exhaustive-deps
}
